// Single source of truth for blog post metadata.
// Consumed by Blog.tsx (listing), RelatedPosts.tsx, and the RSS/sitemap generators.

export interface BlogPostMeta {
  id: string
  title: string
  excerpt: string
  category: string
  categorySlug: string
  author: string
  date: string // ISO yyyy-mm-dd
  readTime: string
  slug: string // path under /blog/, may include a subpath (e.g. tools/nmap)
  featured?: boolean
}

export const blogPosts: BlogPostMeta[] = [
  {
    id: 'the-end-of-msps',
    title: 'The End of MSPs',
    excerpt:
      'Clients now expect a pentest in a day or a week. All-AI is fast but shallow; all-manual is thorough but slow. Semi-automated, human-led engagements are how a firm stays fast and thorough at once.',
    category: 'Opinion',
    categorySlug: 'opinion',
    author: 'Zypheron Team',
    date: '2026-10-16',
    readTime: '7 MIN',
    slug: 'the-end-of-msps',
    featured: true,
  },
  {
    id: 'zypheron-v1-5-update',
    title: 'Zypheron v1.5: Workspace Chat, AD+Cloud Graphs, Real C2 Sessions',
    excerpt:
      'The unreleased cycle after 1.4.2: sidebar @mentions, Network Map overhaul, AD and cloud collectors, 130+ tools, and persisted Metasploit and Sliver sessions.',
    category: 'Product',
    categorySlug: 'product',
    author: 'Zypheron Team',
    date: '2026-09-19',
    readTime: '8 MIN',
    slug: 'zypheron-v1-5-update',
    featured: true,
  },
  {
    id: 'zypheron-methodology',
    title: 'The Zypheron Methodology: AI-Augmented, Human-Led Security Assessment',
    excerpt: 'How AI amplifies a security team\u2019s judgment without replacing it, with a human operator controlling each stage of the assessment.',
    category: 'Methodology',
    categorySlug: 'methodology',
    author: 'Zypheron Team',
    date: '2026-09-19',
    readTime: '10 MIN',
    slug: 'zypheron-methodology',
    featured: true,
  },
  {
    id: 'ad-cloud-recon-playbook',
    title: 'Workflow Playbook: AD + Cloud Recon',
    excerpt: 'Walk the identity boundary from on-prem Active Directory into cloud, then stitch both sides into one attack-path graph.',
    category: 'Workflow',
    categorySlug: 'workflow',
    author: 'Zypheron Team',
    date: '2026-09-19',
    readTime: '12 MIN',
    slug: 'ad-cloud-recon-playbook',
    featured: true,
  },
  {
    id: 'zypheron-v1-4-1-update',
    title: 'Zypheron v1.4.1: Sharper Reasoning, Docker, Fewer AI Pentesting Bugs',
    excerpt:
      'v1.4.1 improves AI chat quality and reasoning, resolves AI-driven pentesting issues, adds Docker support, and hardens the Linux desktop build.',
    category: 'Product',
    categorySlug: 'product',
    author: 'Zypheron Team',
    date: '2026-08-12',
    readTime: '6 MIN',
    slug: 'zypheron-v1-4-1-update',
    featured: true,
  },
  {
    id: 'zypheron-erebus-one-stack',
    title: 'Zypheron + Erebus: One Stack From Recon to C2 Report',
    excerpt:
      'How Zypheron Desktop and Erebus C2 fit together across one engagement: recon, notes, and evidence in Zypheron, controlled sessions in Erebus, one report at the end.',
    category: 'Product',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-08-10',
    readTime: '7 MIN',
    slug: 'zypheron-erebus-one-stack',
    featured: true,
  },
  {
    id: 'erebus-two-person-red-team',
    title: 'Erebus for a 2-Person Red Team: What Changes',
    excerpt:
      'How a 2 or 3 person red team runs engagements with Erebus C2: fewer manual handoffs, one AI-aware control loop, and no seat-based cost tax.',
    category: 'Security',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-08-10',
    readTime: '6 MIN',
    slug: 'erebus-two-person-red-team',
    featured: true,
  },
  {
    id: 'erebus-winrm-pth-message-encryption',
    title: 'Erebus Update: WinRM Pass-the-Hash Message Encryption',
    excerpt:
      'Erebus now seals WinRM SOAP on the NTLM hash (PTH) path for AllowUnencrypted=false hosts, with a plain-SOAP fallback when encryption is rejected.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-08-08',
    readTime: '7 MIN',
    slug: 'erebus-winrm-pth-message-encryption',
    featured: true,
  },
  {
    id: 'what-is-erebus-c2',
    title: 'What Is Erebus C2? AI-Native Command and Control, Explained',
    excerpt:
      'A plain-English explanation of Erebus C2: what it is, who it is for, why it is AI-native, and how operators stay in control during authorized security work.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-08-05',
    readTime: '8 MIN',
    slug: 'what-is-erebus-c2',
    featured: true,
  },
  {
    id: 'why-erebus-is-open-source',
    title: 'Why Erebus Is Open Source',
    excerpt:
      'Why Erebus C2 is open source: auditability, operator trust, safer AI-assisted workflows, and a transparent human-in-the-loop control model.',
    category: 'Security',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-08-05',
    readTime: '8 MIN',
    slug: 'why-erebus-is-open-source',
  },
  {
    id: 'how-erebus-c2-works',
    title: 'How Erebus C2 Works: Teamserver, Implant, CLI, and AI Loop',
    excerpt:
      'A high-level walkthrough of how Erebus C2 works: the Go teamserver, implant lifecycle, operator CLI, task queue, and AI-assisted approval loop.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-08-05',
    readTime: '9 MIN',
    slug: 'how-erebus-c2-works',
  },
  {
    id: 'erebus-1-1',
    title: 'Erebus 1.1: The C Implant Grows Up',
    excerpt:
      'Erebus 1.1 ships a dual-platform C implant: Linux peer, CA pin fixes, millisecond beacons, clearer auth failures, Windows AD soft-path modules, and lab drop scripts—shaped by real eng pain.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-08-05',
    readTime: '8 MIN',
    slug: 'erebus-1-1',
    featured: true,
  },
  {
    id: 'what-is-a-cybersecurity-ide',
    title: 'What Is a Cybersecurity IDE?',
    excerpt: 'A cybersecurity IDE keeps recon, terminal output, notes, evidence, AI assistance, and reporting in one operator workspace.',
    category: 'Cybersecurity IDE',
    categorySlug: 'cybersecurity-ide',
    author: 'Zypheron Team',
    date: '2026-07-29',
    readTime: '7 MIN',
    slug: 'what-is-a-cybersecurity-ide',
    featured: true,
  },
  {
    id: 'best-cybersecurity-ide-for-beginners',
    title: 'Best Cybersecurity IDE for Beginners: What to Look For',
    excerpt: 'Beginners need a cybersecurity IDE that keeps real tools visible while organizing scope, output, evidence, notes, and first reports.',
    category: 'Cybersecurity IDE',
    categorySlug: 'cybersecurity-ide',
    author: 'Zypheron Team',
    date: '2026-07-29',
    readTime: '7 MIN',
    slug: 'best-cybersecurity-ide-for-beginners',
  },
  {
    id: 'zypheron-for-bug-bounty-beginners',
    title: 'Using Zypheron for Your First Bug Bounty Recon Workflow',
    excerpt: 'A beginner-friendly bug bounty workflow for Zypheron Desktop: scope, authorized recon, notes, evidence, validation, and a clean finding path.',
    category: 'Tutorials',
    categorySlug: 'tutorials',
    author: 'Zypheron Team',
    date: '2026-07-29',
    readTime: '8 MIN',
    slug: 'zypheron-for-bug-bounty-beginners',
  },
  {
    id: 'homelab-pentest-workspace',
    title: 'How to Build a Pentest Homelab Workspace Without Tool Sprawl',
    excerpt: 'Homelab operators learn faster when authorized targets, commands, notes, evidence, and lessons learned stay in one repeatable workspace.',
    category: 'Tutorials',
    categorySlug: 'tutorials',
    author: 'Zypheron Team',
    date: '2026-07-29',
    readTime: '7 MIN',
    slug: 'homelab-pentest-workspace',
  },
  {
    id: 'learn-pentesting-with-one-workspace',
    title: 'Learn Pentesting Faster With One Workspace Instead of Five Apps',
    excerpt: 'One workspace gives pentest learners a tighter feedback loop across recon, terminal context, evidence, notes, and report practice.',
    category: 'Career',
    categorySlug: 'career',
    author: 'Zypheron Team',
    date: '2026-07-29',
    readTime: '7 MIN',
    slug: 'learn-pentesting-with-one-workspace',
  },
  {
    id: 'cybersecurity-notes-evidence-reports',
    title: 'Why Security Notes, Evidence, and Reports Belong in the Same Workspace',
    excerpt: 'Security notes are more useful when they stay connected to commands, screenshots, assets, findings, and reports.',
    category: 'Workflow',
    categorySlug: 'workflow',
    author: 'Zypheron Team',
    date: '2026-07-29',
    readTime: '7 MIN',
    slug: 'cybersecurity-notes-evidence-reports',
  },
  {
    id: 'local-ai-cybersecurity-ide',
    title: 'Local AI for Cybersecurity: Why Your IDE Should Keep Sensitive Data Close',
    excerpt: 'Local AI matters when security work includes sensitive targets, recon output, notes, evidence, and findings that should not default to SaaS.',
    category: 'AI',
    categorySlug: 'ai',
    author: 'Zypheron Team',
    date: '2026-07-29',
    readTime: '8 MIN',
    slug: 'local-ai-cybersecurity-ide',
  },
  {
    id: 'nmap-nuclei-burp-one-workspace',
    title: 'Nmap, Nuclei, Burp, and Notes: How to Keep Recon Organized',
    excerpt: 'A practical way to keep Nmap, Nuclei, Burp, screenshots, notes, and validation evidence organized in one Zypheron workspace.',
    category: 'Workflow',
    categorySlug: 'workflow',
    author: 'Zypheron Team',
    date: '2026-07-29',
    readTime: '8 MIN',
    slug: 'nmap-nuclei-burp-one-workspace',
  },
  {
    id: 'junior-pentester-workflow',
    title: 'A Junior Pentester Workflow That Looks Senior',
    excerpt: 'A practical junior pentester workflow for scope, recon, validation, evidence, impact, remediation, and report notes.',
    category: 'Career',
    categorySlug: 'career',
    author: 'Zypheron Team',
    date: '2026-07-29',
    readTime: '7 MIN',
    slug: 'junior-pentester-workflow',
  },
  {
    id: 'zypheron-vs-kali-terminal-workflow',
    title: 'Zypheron vs a Pure Kali Terminal Workflow',
    excerpt: 'Kali Linux is strong for raw terminal work. Zypheron adds the workspace layer around evidence, notes, AI context, and reporting.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-07-29',
    readTime: '8 MIN',
    slug: 'zypheron-vs-kali-terminal-workflow',
    featured: true,
  },
  {
    id: 'pentest-workflow-leaking-time-before-reporting',
    title: 'Your Pentest Workflow Is Leaking Time Before Reporting Starts',
    excerpt: 'Pentest firms lose time when evidence, notes, commands, screenshots, and findings live in different places before report week begins.',
    category: 'Workflow',
    categorySlug: 'workflow',
    author: 'Zypheron Team',
    date: '2026-07-14',
    readTime: '6 MIN',
    slug: 'pentest-workflow-leaking-time-before-reporting',
    featured: true,
  },
  {
    id: 'real-risk-finding-you-cannot-prove-later',
    title: 'The Real Risk Is The Finding You Cannot Prove Later',
    excerpt: 'Unsupported findings are hard to defend. Internal security teams need proof tied to each claim before the report reaches leadership.',
    category: 'Workflow',
    categorySlug: 'workflow',
    author: 'Zypheron Team',
    date: '2026-07-14',
    readTime: '6 MIN',
    slug: 'real-risk-finding-you-cannot-prove-later',
  },
  {
    id: 'why-small-security-teams-need-repeatable-assessments',
    title: 'Why Lean Security Teams Need Repeatable Assessments',
    excerpt: 'Lean teams need a repeatable assessment loop so evidence, findings, and leadership updates do not depend on one busy person.',
    category: 'Workflow',
    categorySlug: 'workflow',
    author: 'Zypheron Team',
    date: '2026-07-14',
    readTime: '6 MIN',
    slug: 'why-small-security-teams-need-repeatable-assessments',
  },
  {
    id: 'best-pentester-should-not-be-whole-process',
    title: 'Your Best Pentester Should Not Be The Whole Process',
    excerpt: 'Pentest firms scale quality when senior habits become a repeatable workflow with attached evidence and easier QA.',
    category: 'Workflow',
    categorySlug: 'workflow',
    author: 'Zypheron Team',
    date: '2026-07-14',
    readTime: '6 MIN',
    slug: 'best-pentester-should-not-be-whole-process',
  },
  {
    id: 'what-a-zypheron-workflow-review-covers',
    title: 'What A Zypheron Workflow Review Covers',
    excerpt: 'A practical look at what happens in a workflow review for pentest firms and internal security teams.',
    category: 'Buying Guide',
    categorySlug: 'buying-guide',
    author: 'Zypheron Team',
    date: '2026-07-14',
    readTime: '5 MIN',
    slug: 'what-a-zypheron-workflow-review-covers',
    featured: true,
  },
  {
    id: 'from-scan-output-to-client-ready-finding-in-zypheron',
    title: 'From Scan Output To Client-Ready Finding In Zypheron',
    excerpt: 'How Zypheron carries scan output through evidence, review, and reporting so findings are ready for client handoff.',
    category: 'Product',
    categorySlug: 'product',
    author: 'Zypheron Team',
    date: '2026-07-14',
    readTime: '6 MIN',
    slug: 'from-scan-output-to-client-ready-finding-in-zypheron',
  },
  {
    id: 'how-zypheron-keeps-operators-in-control',
    title: 'How Zypheron Keeps Operators In Control',
    excerpt: 'How Zypheron keeps human operators in control of model choice, approvals, evidence, and final report language.',
    category: 'Product',
    categorySlug: 'product',
    author: 'Zypheron Team',
    date: '2026-07-14',
    readTime: '6 MIN',
    slug: 'how-zypheron-keeps-operators-in-control',
  },
  {
    id: 'how-to-know-if-zypheron-fits-your-team',
    title: 'How To Know If Zypheron Fits Your Team',
    excerpt: 'A fit check for teams with reporting pain, strict data rules, and assessment work that needs clearer proof.',
    category: 'Buying Guide',
    categorySlug: 'buying-guide',
    author: 'Zypheron Team',
    date: '2026-07-14',
    readTime: '6 MIN',
    slug: 'how-to-know-if-zypheron-fits-your-team',
  },
  {
    id: 'erebus-vs-metasploit',
    title: 'Erebus vs Metasploit: AI-Native C2 vs Exploit Framework Workflows',
    excerpt: 'A realistic comparison of coming-soon Erebus and Metasploit for pentest firms evaluating AI-native C2 workflows, exploit modules, operator review, and reporting handoff.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-06-26',
    readTime: '8 MIN',
    slug: 'erebus-vs-metasploit',
    featured: true,
  },
  {
    id: 'erebus-vs-sliver',
    title: 'Erebus vs Sliver: AI-Native C2 for Operator-Controlled Engagements',
    excerpt: 'Sliver is a serious open-source C2. Erebus is being built for structured AI-assisted tasking, operator review, and cleaner evidence continuity inside Zypheron.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-06-26',
    readTime: '8 MIN',
    slug: 'erebus-vs-sliver',
  },
  {
    id: 'erebus-vs-havoc',
    title: 'Erebus vs Havoc: Modern C2 Design, AI Workflows, and Operator Review',
    excerpt: 'Havoc represents modern C2 usability. Erebus is aimed at the next question: what changes when C2 is designed for AI-assisted operators from the start?',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-06-26',
    readTime: '7 MIN',
    slug: 'erebus-vs-havoc',
  },
  {
    id: 'erebus-vs-cobalt-strike',
    title: 'Erebus vs Cobalt Strike: Coming-Soon AI-Native C2 vs Paid Red Team Platform',
    excerpt: 'Cobalt Strike is mature and paid. Erebus is positioned for small firms that want AI-native tasking, explicit operator review, and stronger deliverable continuity.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-06-26',
    readTime: '8 MIN',
    slug: 'erebus-vs-cobalt-strike',
  },
  {
    id: 'zypheron-vs-vs-code',
    title: 'Zypheron vs VS Code: Cybersecurity IDE vs General-Purpose Code Editor',
    excerpt: 'VS Code is an excellent editor. Zypheron is built for the narrower job pentest firms actually ship: evidence, findings, AI assistance, and reports.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-06-26',
    readTime: '7 MIN',
    slug: 'zypheron-vs-vs-code',
  },
  {
    id: 'zypheron-vs-tmux-vim',
    title: 'Zypheron vs tmux and Vim: One Pentest Workspace vs Terminal-First Stacks',
    excerpt: 'tmux and Vim are fast and powerful. Zypheron solves the firm workflow around them: structured evidence, reviewable context, and client-ready reporting.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-06-26',
    readTime: '7 MIN',
    slug: 'zypheron-vs-tmux-vim',
  },
  {
    id: 'zypheron-vs-manticore-ai',
    title: 'Zypheron vs Manticore AI: Local-First Pentest Workflow vs AI Security Automation',
    excerpt: 'A careful comparison for buyers evaluating AI security automation, local-first assessment data, operator control, and defensible reporting.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-06-26',
    readTime: '7 MIN',
    slug: 'zypheron-vs-manticore-ai',
  },
  {
    id: 'zypheron-vs-openvas',
    title: 'Zypheron vs OpenVAS: Evidence-Driven Pentest Workflow vs Vulnerability Scanner',
    excerpt: 'OpenVAS is useful vulnerability scanning. Zypheron focuses on what comes next: triage, evidence, findings, AI-assisted analysis, and reports.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-06-26',
    readTime: '7 MIN',
    slug: 'zypheron-vs-openvas',
  },
  {
    id: 'zypheron-vs-nessus',
    title: 'Zypheron vs Nessus: Pentest Reporting Workspace vs Commercial Vulnerability Scanner',
    excerpt: 'Nessus is mature commercial scanning. Zypheron is the workspace for turning technical output into evidence-backed pentest deliverables.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-06-26',
    readTime: '8 MIN',
    slug: 'zypheron-vs-nessus',
  },
  {
    id: 'zypheron-vs-bloodhound',
    title: 'Zypheron vs BloodHound: Attack Path Mapping vs Full Assessment Workspace',
    excerpt: 'BloodHound built graph-based identity attack path analysis. Zypheron ships an attack path graph as one workspace view alongside recon, evidence, and reporting.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-07-06',
    readTime: '8 MIN',
    slug: 'zypheron-vs-bloodhound',
    featured: true,
  },
  {
    id: 'zypheron-vs-plextrac',
    title: 'Zypheron vs PlexTrac: Operator Workspace vs Pentest Reporting and Management Platform',
    excerpt: 'PlexTrac consolidates findings from many testers and tools. Zypheron is the workspace where testing and reporting already happen together for lean teams.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-07-06',
    readTime: '8 MIN',
    slug: 'zypheron-vs-plextrac',
  },
  {
    id: 'zypheron-vs-burp-suite',
    title: 'Zypheron vs Burp Suite: Full Assessment Workspace vs the Web Pentesting Standard',
    excerpt: 'Burp Suite Professional is the web testing standard. Zypheron carries confirmed findings into evidence, notes, and the final client deliverable.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-07-06',
    readTime: '7 MIN',
    slug: 'zypheron-vs-burp-suite',
  },
  {
    id: 'what-is-c2-infrastructure',
    title: 'What Is C2 Infrastructure? A Plain Definition for Operators and Buyers',
    excerpt: 'C2 is the channel an operator uses to task, control, and receive output from an implant. What it is, how beaconing works, and where it fits in an engagement.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-06',
    readTime: '6 MIN',
    slug: 'what-is-c2-infrastructure',
  },
  {
    id: 'what-is-attack-path-mapping',
    title: 'What Is Attack Path Mapping? Identity Graphs, Explained',
    excerpt: 'Attack path mapping graphs identities, permissions, and trust to show how an attacker reaches Domain Admin or a cloud admin role from a standard user account.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-06',
    readTime: '6 MIN',
    slug: 'what-is-attack-path-mapping',
  },
  {
    id: 'what-is-purple-teaming',
    title: 'What Is Purple Teaming? Red and Blue Working the Same Loop',
    excerpt: 'Purple teaming runs red and blue together in real time so detection gaps get tuned in the same session instead of surfacing weeks later in a report.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-06',
    readTime: '6 MIN',
    slug: 'what-is-purple-teaming',
  },
  {
    id: 'best-c2-frameworks-2026',
    title: 'Best C2 Frameworks 2026: A Practical Comparison',
    excerpt: 'A ranked look at Metasploit, Sliver, Havoc, Cobalt Strike, Brute Ratel, and the coming-soon AI-native Erebus, and which one actually fits your engagement.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-06',
    readTime: '9 MIN',
    slug: 'best-c2-frameworks-2026',
    featured: true,
  },
  {
    id: 'best-pentest-reporting-tools-2026',
    title: 'Best Pentest Reporting Tools 2026: What Actually Speeds Up Delivery',
    excerpt: 'PlexTrac, Dradis, templates, or a workspace-native tool like Zypheron. Which reporting approach fits your team size and concurrent engagement count.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-06',
    readTime: '8 MIN',
    slug: 'best-pentest-reporting-tools-2026',
  },
  {
    id: 'state-of-internal-pentest-tooling-2026',
    title: 'The State of Internal Pentest Tooling 2026: What the Data Says',
    excerpt: 'Public 2026 industry data on tool fragmentation, remediation timelines, and program cost, and what a 25x gap between top and bottom performers actually means.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-06',
    readTime: '9 MIN',
    slug: 'state-of-internal-pentest-tooling-2026',
    featured: true,
  },
  {
    id: 'zypheron-vs-dradis',
    title: 'Zypheron vs Dradis: Assessment Workspace vs Pentest Management Platform',
    excerpt: 'Dradis is strong for self-hosted pentest management and reporting. Zypheron focuses on the operator workspace where evidence, notes, attack paths, and reports start together.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '8 MIN',
    slug: 'zypheron-vs-dradis',
    featured: true,
  },
  {
    id: 'zypheron-vs-faraday',
    title: 'Zypheron vs Faraday: Lean Assessment Workspace vs Offensive Security Platform',
    excerpt: 'Faraday positions around unified offensive security, vulnerability management, and reporting. Zypheron is narrower: local-first assessment work for lean teams.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '8 MIN',
    slug: 'zypheron-vs-faraday',
  },
  {
    id: 'best-active-directory-security-tools-2026',
    title: 'Best Active Directory Security Tools 2026: A Use-Case Guide',
    excerpt: 'BloodHound, PingCastle, Purple Knight, Microsoft exposure tooling, and Zypheron each solve a different part of Active Directory security assessment.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '10 MIN',
    slug: 'best-active-directory-security-tools-2026',
    featured: true,
  },
  {
    id: 'best-attack-path-management-tools-2026',
    title: 'Best Attack Path Management Tools 2026: Mapping, Monitoring, and Reporting',
    excerpt: 'Attack path tools range from identity graph platforms to exposure management suites. This guide separates continuous monitoring from engagement delivery.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '10 MIN',
    slug: 'best-attack-path-management-tools-2026',
  },
  {
    id: 'best-bloodhound-alternatives',
    title: 'Best BloodHound Alternatives: When You Need More Than an Identity Graph',
    excerpt: 'BloodHound is still the reference point for identity attack paths. Alternatives depend on whether you need continuous monitoring, AD scoring, or report delivery.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '8 MIN',
    slug: 'best-bloodhound-alternatives',
  },
  {
    id: 'what-is-active-directory-security-assessment',
    title: 'What Is an Active Directory Security Assessment?',
    excerpt: 'An AD security assessment looks at identities, privileges, attack paths, Tier Zero exposure, and evidence that helps teams fix the right risks first.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '6 MIN',
    slug: 'what-is-active-directory-security-assessment',
  },
  {
    id: 'what-is-identity-threat-detection-response',
    title: 'What Is Identity Threat Detection and Response?',
    excerpt: 'ITDR focuses on identity abuse, credential misuse, and risky privilege paths. Here is how it differs from assessment work and vulnerability scanning.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '6 MIN',
    slug: 'what-is-identity-threat-detection-response',
  },
  {
    id: 'what-is-attack-path-management',
    title: 'What Is Attack Path Management?',
    excerpt: 'Attack path management is the ongoing practice of finding, prioritizing, and reducing paths an attacker could use to reach critical assets.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '6 MIN',
    slug: 'what-is-attack-path-management',
  },
  {
    id: 'turn-nmap-nessus-bloodhound-output-into-report',
    title: 'How to Turn Nmap, Nessus, and BloodHound Output Into a Report',
    excerpt: 'Scanner output and graph paths are not a report by themselves. This workflow turns tool output into evidence-backed technical and executive findings.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '9 MIN',
    slug: 'turn-nmap-nessus-bloodhound-output-into-report',
    featured: true,
  },
  {
    id: 'prioritize-active-directory-findings',
    title: 'How to Prioritize Active Directory Findings',
    excerpt: 'Prioritizing AD findings means weighing Tier Zero impact, path length, exploitability, blast radius, and whether one fix breaks many paths.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '8 MIN',
    slug: 'prioritize-active-directory-findings',
  },
  {
    id: 'best-internal-pentest-tools-2026',
    title: 'Best Internal Pentest Tools 2026: A Use-Case Map for Lean Teams',
    excerpt: 'Internal pentest tooling is a stack: scanners, graph tools, C2, notes, reporting, and validation. The right choice depends on the workflow gap.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '10 MIN',
    slug: 'best-internal-pentest-tools-2026',
  },
  {
    id: 'best-pentest-tools-small-security-teams',
    title: 'Best Pentest Tools for Small Security Teams',
    excerpt: 'Small security teams need tools that reduce handoffs, preserve evidence, and avoid turning every assessment into a reporting rebuild.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '8 MIN',
    slug: 'best-pentest-tools-small-security-teams',
  },
  {
    id: 'best-dradis-alternatives',
    title: 'Best Dradis Alternatives: Reporting, Collaboration, and Workspace Options',
    excerpt: 'Dradis is a strong self-hosted reporting and management option. Alternatives depend on whether you need vulnerability management, SaaS workflows, or a local workspace.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '7 MIN',
    slug: 'best-dradis-alternatives',
  },
  {
    id: 'best-faraday-alternatives',
    title: 'Best Faraday Alternatives: Offensive Security Platform Options',
    excerpt: 'Faraday covers vulnerability management, reporting, and offensive workflows. Alternatives vary by team size, data-control needs, and report workflow.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '7 MIN',
    slug: 'best-faraday-alternatives',
  },
  {
    id: 'erebus-vs-brute-ratel',
    title: 'Erebus vs Brute Ratel: AI-Native C2 Direction vs Adversary Simulation Platform',
    excerpt: 'Brute Ratel focuses on commercial adversary simulation and C2 tradecraft. Erebus is being designed around AI-readable tasking and evidence continuity.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '8 MIN',
    slug: 'erebus-vs-brute-ratel',
  },
  {
    id: 'erebus-vs-mythic',
    title: 'Erebus vs Mythic: AI-Native C2 Direction vs Open-Source Operator Platform',
    excerpt: 'Mythic is a collaborative open-source C2 platform. Erebus is aimed at structured AI-assisted operations and cleaner handoff into Zypheron evidence.',
    category: 'Comparisons',
    categorySlug: 'comparisons',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '8 MIN',
    slug: 'erebus-vs-mythic',
  },
  {
    id: 'what-is-assumed-breach-assessment',
    title: 'What Is an Assumed Breach Assessment?',
    excerpt: 'An assumed breach assessment starts from the premise that an attacker already has a foothold, then tests lateral movement, privilege paths, and detection.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '6 MIN',
    slug: 'what-is-assumed-breach-assessment',
  },
  {
    id: 'what-is-tier-zero-active-directory',
    title: 'What Is Tier Zero in Active Directory?',
    excerpt: 'Tier Zero covers the identities and systems that can control the directory. Protecting it starts with understanding every path into it.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '6 MIN',
    slug: 'what-is-tier-zero-active-directory',
  },
  {
    id: 'scope-internal-network-pentest',
    title: 'How to Scope an Internal Network Pentest',
    excerpt: 'Good scope defines assets, identities, testing windows, exclusions, success criteria, and reporting outputs before the first scan runs.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '7 MIN',
    slug: 'scope-internal-network-pentest',
  },
  {
    id: 'validate-remediation-after-pentest',
    title: 'How to Validate Remediation After a Pentest',
    excerpt: 'Retesting is more than checking a box. It should prove the path is broken, the fix is durable, and the evidence is ready for leadership or auditors.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-08',
    readTime: '7 MIN',
    slug: 'validate-remediation-after-pentest',
  },
  {
    id: 'erebus-is-out',
    title: 'Erebus Is Out',
    excerpt: 'Erebus is live and open source on GitHub. An AI-native C2 framework, human-in-the-loop by design, solo-built and validated against non-trivial HTB engagements.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-31',
    readTime: '5 MIN',
    slug: 'erebus-is-out',
    featured: true,
  },
  {
    id: 'erebus-architecture',
    title: "Inside Erebus: How It's Built",
    excerpt: 'A technical look at Erebus: the Go teamserver, cross-platform implant, operator CLI, and AI agent layer that make up the open source C2 framework.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-07-31',
    readTime: '7 MIN',
    slug: 'erebus-architecture',
    featured: true,
  },
  {
    id: 'erebus-ai-native-design',
    title: 'Why AI-Native, Not AI-Bolted-On',
    excerpt: 'AI-native design and human-in-the-loop approval gates are why Erebus is trustworthy for authorized offensive work. Here is the reasoning behind the architecture.',
    category: 'Security',
    categorySlug: 'security',
    author: 'Zypheron Team',
    date: '2026-07-31',
    readTime: '6 MIN',
    slug: 'erebus-ai-native-design',
    featured: true,
  },
  {
    id: 'erebus-getting-closer',
    title: 'Erebus Is Getting Closer',
    excerpt: 'A quiet progress note on Erebus: the operator experience is tightening, the trust model is getting clearer, and the next release is moving closer without exposing unreleased internals.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-06-25',
    readTime: '4 MIN',
    slug: 'erebus-getting-closer',
    featured: true,
  },
  {
    id: 'five-tool-tax-pentest-engagements',
    title: 'The 5-Tool Tax: Where Pentest Engagements Lose Time',
    excerpt: 'Most pentest delays come from the spaces between tools: scanner output, terminal context, graph paths, notes, screenshots, and the final report all living in separate places.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-06-23',
    readTime: '8 MIN',
    slug: 'five-tool-tax-pentest-engagements',
    featured: true,
  },
  {
    id: 'board-ready-internal-security-assessment',
    title: 'What Your Board Actually Needs From an Internal Security Assessment',
    excerpt: 'Boards do not need raw scanner output. They need risk, impact, remediation priority, and traceable evidence that turns assessment work into a decision.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-06-22',
    readTime: '7 MIN',
    slug: 'board-ready-internal-security-assessment',
    featured: true,
  },
  {
    id: 'first-internal-ad-assessment',
    title: 'A Realistic First Internal AD Assessment for a Two-Person Security Team',
    excerpt: 'A lean internal security team can run a useful Active Directory assessment by scoping the first pass, preserving evidence, and shipping a report leadership can act on.',
    category: 'Tutorials',
    categorySlug: 'tutorials',
    author: 'Zypheron Team',
    date: '2026-06-21',
    readTime: '9 MIN',
    slug: 'first-internal-ad-assessment',
    featured: true,
  },
  {
    id: 'pentest-report-writing-evidence-recovery',
    title: 'Pentest Report Writing Is Not Writing. It Is Evidence Recovery.',
    excerpt: 'Most report pain comes from recovering scattered proof after the engagement. Better reporting starts with capturing commands, screenshots, assets, and impact as the work happens.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-06-20',
    readTime: '8 MIN',
    slug: 'pentest-report-writing-evidence-recovery',
  },
  {
    id: 'security-data-should-not-default-to-saas',
    title: 'Why Security Data Should Not Default to SaaS',
    excerpt: 'Assessment data includes exploit paths, credentials, topology, and sensitive findings. Local-first workflows should be the default for many teams and firms.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-06-19',
    readTime: '7 MIN',
    slug: 'security-data-should-not-default-to-saas',
  },
  {
    id: 'copilot-vs-autonomous-security-agent',
    title: 'The Difference Between a Copilot and an Autonomous Security Agent',
    excerpt: 'Security teams need clear AI boundaries. A copilot accelerates and explains; an autonomous agent acts. For assessment work, that trust boundary matters.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-06-18',
    readTime: '8 MIN',
    slug: 'copilot-vs-autonomous-security-agent',
  },
  {
    id: 'quarterly-security-assessment-checklist',
    title: 'The Quarterly Security Assessment Checklist for 50-500 Person Companies',
    excerpt: 'A repeatable quarterly assessment rhythm for lean internal teams: scope, collect evidence, prioritize paths, report clearly, and compare against last quarter.',
    category: 'Tutorials',
    categorySlug: 'tutorials',
    author: 'Zypheron Team',
    date: '2026-06-17',
    readTime: '10 MIN',
    slug: 'quarterly-security-assessment-checklist',
  },
  {
    id: 'client-ready-pentest-deliverables',
    title: 'Client-Ready Pentest Deliverables: What to Capture Before the Report',
    excerpt: 'Pentest firms can make reports faster and stronger by capturing client-ready evidence during the engagement instead of reconstructing it after testing ends.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-06-16',
    readTime: '8 MIN',
    slug: 'client-ready-pentest-deliverables',
  },
  {
    id: 'compliance-dashboard-frameworks',
    title: 'Automatically Map Pentest Findings to SOC 2, HIPAA, PCI, and NIST',
    excerpt: 'The new Compliance Dashboard in Zypheron Desktop maps your live findings to seven control frameworks: NIST 800-53, CIS v8, OWASP, PCI DSS v4, ISO 27001, HIPAA, and SOC 2, with coverage and pass-rate at a glance.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-06-15',
    readTime: '7 MIN',
    slug: 'compliance-dashboard-frameworks',
    featured: true,
  },
  {
    id: 'pentest-report-templates',
    title: 'Technical, Executive, and Compliance Reports From One Workspace',
    excerpt: 'The Reports page turns your findings into Technical, Executive, or Compliance reports and exports to PDF, HTML, Markdown, or JSON, with evidence collated automatically by the Auto-Doc panel.',
    category: 'Tutorials',
    categorySlug: 'tutorials',
    author: 'Zypheron Team',
    date: '2026-06-12',
    readTime: '7 MIN',
    slug: 'pentest-report-templates',
    featured: true,
  },
  {
    id: 'cli-desktop-pairing',
    title: 'Pair the CLI to the Desktop: One Login, Shared Findings',
    excerpt: 'One browser login, a scoped token in your OS keyring, an approval modal, and CLI scan results that show up in your desktop workspaces. How the free CLI and the desktop app connect.',
    category: 'Tutorials',
    categorySlug: 'tutorials',
    author: 'Zypheron Team',
    date: '2026-06-10',
    readTime: '6 MIN',
    slug: 'cli-desktop-pairing',
  },
  {
    id: 'automate-pentest-reporting',
    title: 'How to Automate Pentest Reporting (Without Losing the Detail)',
    excerpt: 'Report writing eats a quarter to a half of every engagement. Capture evidence as you work, generate Technical, Executive, and Compliance reports, and export to PDF, HTML, Markdown, or JSON from the CLI or desktop.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-06-08',
    readTime: '13 MIN',
    slug: 'automate-pentest-reporting',
  },
  {
    id: 'compliance-evidence-from-scans',
    title: 'Compliance Evidence Auditors Accept: Straight From Your Scans',
    excerpt: 'What auditors want is evidence tied to controls, not a raw scan. Turn findings into SOC 2, PCI DSS, ISO 27001, and NIST evidence with coverage and pass-rate you can defend.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-06-05',
    readTime: '14 MIN',
    slug: 'compliance-evidence-from-scans',
  },
  {
    id: 'local-first-pentest-data',
    title: 'Local-First Security: Why Pentest Data Should Never Hit a SaaS',
    excerpt: 'Pentest data is the most sensitive thing your team produces. Encrypted local storage, bring-your-own AI key, and offline models via Ollama keep findings on your machine instead of someone else\'s cloud.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-06-03',
    readTime: '12 MIN',
    slug: 'local-first-pentest-data',
  },
  {
    id: 'first-30-minutes',
    title: 'The First 30 Minutes After Installing Zypheron Desktop',
    excerpt: 'From a fresh install to your first real finding: confirm the install, open a workspace, run one scan, and ground the AI in what you found. The bridge between download and first result.',
    category: 'Tutorials',
    categorySlug: 'tutorials',
    author: 'Zypheron Team',
    date: '2026-05-28',
    readTime: '6 MIN',
    slug: 'first-30-minutes',
    featured: true,
  },
  {
    id: 'recon-to-notes-to-report',
    title: 'From Recon to Notes to Report: One Workspace Instead of Five',
    excerpt: 'A scanner, a graph tool, a terminal, a notes doc, and a report template. Five seams where context dies. Here is what it looks like when the workspace carries the thread for you.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-05-26',
    readTime: '7 MIN',
    slug: 'recon-to-notes-to-report',
  },
  {
    id: 'fragmented-stack-vs-workspace',
    title: 'Terminal + Graph Tool + RE Tool + Notes Doc vs One Operator Workspace',
    excerpt: 'One app will not replace your specialist tools. The point is simpler: the gaps between them are where your time and your findings leak out.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-05-22',
    readTime: '6 MIN',
    slug: 'fragmented-stack-vs-workspace',
  },
  {
    id: 'internal-teams-standardize-workflows',
    title: 'How Small Internal Security Teams Standardize Offensive Workflows',
    excerpt: 'Repeatable coverage of Active Directory and identity weaknesses for a two or three person team, driven by ATT&CK profiles and shared findings. No $50k pentest required.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-05-20',
    readTime: '7 MIN',
    slug: 'internal-teams-standardize-workflows',
  },
  {
    id: 'install-and-forget-fails',
    title: 'Why Install and Forget Security Tools Fail Active Operators',
    excerpt: 'Dashboards that run in the background and email a score are not the same as a workspace you operate. Why passive tooling fails the people doing the actual work.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-05-18',
    readTime: '6 MIN',
    slug: 'install-and-forget-fails',
  },
  {
    id: 'zypheron-desktop-linux-ga',
    title: 'Zypheron Desktop Is GA on Linux',
    excerpt: 'The Cybersecurity IDE is generally available on Linux: AppImage, .deb, .rpm, GPG-signed and auto-updating. The free download unlocks workspace views; scanning, tools, and reports require a paid plan.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-05-15',
    readTime: '5 MIN',
    slug: 'zypheron-desktop-linux-ga',
    featured: true,
  },
  {
    id: 'reverse-engineering-local-llm',
    title: 'Reverse Engineering with Local-LLM Copilots',
    excerpt: 'Headless Ghidra, hex preview, symbol extraction, and a local model that explains what it found. Same workspace, no egress required.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-05-13',
    readTime: '7 MIN',
    slug: 'reverse-engineering-local-llm',
  },
  {
    id: 'ad-cloud-attack-paths',
    title: 'One Graph for AD and Cloud Attack Paths',
    excerpt: 'On-prem AD, Entra ID, and AWS/Azure/GCP trust in a single graph you can walk. Collapsing the identity boundary attackers ignore.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-05-11',
    readTime: '7 MIN',
    slug: 'ad-cloud-attack-paths',
  },
  {
    id: 'the-cybersecurity-ide',
    title: 'Why We Built a Cybersecurity IDE',
    excerpt: 'The case for one workspace instead of a graph tool, a terminal, a disassembler, and a notes doc. The thesis behind Zypheron Desktop.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-05-09',
    readTime: '6 MIN',
    slug: 'the-cybersecurity-ide',
  },
  {
    id: 'source-bootstrap-vs-release-installer',
    title: 'Source Bootstrap vs Release Installer',
    excerpt: 'The two repo-backed Zypheron install paths solve different problems. This breaks down when to use setup-hybrid.sh and when to use the packaged installer.',
    category: 'Tutorials',
    categorySlug: 'tutorials',
    author: 'Zypheron Team',
    date: '2026-03-15',
    readTime: '5 MIN',
    slug: 'source-bootstrap-vs-release-installer',
  },
  {
    id: 'zypheron-doctor-guide',
    title: 'What zypheron doctor Actually Checks',
    excerpt: 'The fastest path to diagnosing a broken install, missing dependencies, and local toolchain drift before you chase the wrong issue.',
    category: 'Tutorials',
    categorySlug: 'tutorials',
    author: 'Zypheron Team',
    date: '2026-03-15',
    readTime: '6 MIN',
    slug: 'zypheron-doctor-guide',
  },
  {
    id: 'ollama-local-ai-guide',
    title: 'Using Ollama as the Local AI Backend',
    excerpt: 'How the current repo-backed AI flow handles local models, provider persistence, and verification before you move into higher-level workflows.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-03-15',
    readTime: '5 MIN',
    slug: 'ollama-local-ai-guide',
  },
  {
    id: 'ai-native-c2-design',
    title: 'Why Every C2 Framework Falls Short, and What AI-Native Design Actually Looks Like',
    excerpt: 'Traditional C2 frameworks are built for human hands and eyes. Here\'s why forcing AI agents to use them is fundamentally flawed, and how true AI-native design changes everything.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Harrison McCall',
    date: '2026-03-14',
    readTime: '6 MIN',
    slug: 'ai-native-c2-design',
    featured: true,
  },
  {
    id: 'ai-assisted-bug-bounty',
    title: 'AI-Assisted Bug Bounty Recon: The Operator\'s Guide',
    excerpt: 'How security researchers use local LLMs for faster reconnaissance, vulnerability analysis, and reviewable findings without losing control.',
    category: 'Bug Bounty',
    categorySlug: 'bug-bounty',
    author: 'Zypheron Team',
    date: '2026-02-04',
    readTime: '12 MIN',
    slug: 'ai-assisted-bug-bounty',
    featured: true,
  },
  {
    id: 'ai-security-tools-2026',
    title: 'AI Security Tools 2026: Comparative Analysis',
    excerpt: 'Technical breakdown of current AI-assisted security tooling for teams and firms. Where Zypheron fits against continuous validation and specialist platforms.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Zypheron Team',
    date: '2026-02-03',
    readTime: '15 MIN',
    slug: 'ai-security-tools-2026',
    featured: true,
  },
  {
    id: 'nuclei-ai-automation',
    title: 'Nuclei Automation Protocol',
    excerpt: 'Chaining Nuclei templates with AI logic for context-aware scanning. Cuts false positives by correlating and verifying findings before they reach your report.',
    category: 'Tutorials',
    categorySlug: 'tutorials',
    author: 'Harrison McCall',
    date: '2026-02-02',
    readTime: '10 MIN',
    slug: 'nuclei-ai-automation',
  },
  {
    id: 'nmap-integration',
    title: 'Documentation: Nmap Integration',
    excerpt: 'Official documentation for the Nmap module. Flags, syntax, and AI-parsing capabilities for network mapping.',
    category: 'Tool Docs',
    categorySlug: 'tools',
    author: 'Zypheron Team',
    date: '2026-02-01',
    readTime: 'GUIDE',
    slug: 'tools/nmap',
  },
  {
    id: 'metasploit-integration',
    title: 'Documentation: Metasploit RPC',
    excerpt: 'Setting up MSFRPCD for AI interaction. Automating post-exploitation modules safely.',
    category: 'Tool Docs',
    categorySlug: 'tools',
    author: 'Zypheron Team',
    date: '2026-02-01',
    readTime: 'GUIDE',
    slug: 'tools/metasploit',
  },
  {
    id: 'mcp-integration-guide',
    title: 'MCP Integration Standard',
    excerpt: 'Implementing the Model Context Protocol for custom tool definitions. Extend Zypheron\'s capabilities with local scripts.',
    category: 'Engineering',
    categorySlug: 'engineering',
    author: 'Harrison McCall',
    date: '2025-01-23',
    readTime: '15 MIN',
    slug: 'mcp-integration-guide',
  },
]

/** Up to `limit` posts related to `slug`, preferring same category, filling with most-recent others. */
export function getRelatedPosts(slug: string, limit = 3): BlogPostMeta[] {
  const current = blogPosts.find((p) => p.slug === slug)
  const others = blogPosts.filter((p) => p.slug !== slug)
  const sameCategory = current ? others.filter((p) => p.categorySlug === current.categorySlug) : []
  const rest = others.filter((p) => !sameCategory.includes(p))
  return [...sameCategory, ...rest].slice(0, limit)
}
